import React, { useRef, useState } from "react";
import { Link as RouterLink } from "react-router-dom";
import PropTypes from "prop-types";
import { makeStyles } from "@material-ui/core/styles";
import { useTheme } from "@material-ui/core/styles";
import {
	colors,
	Hidden,
	IconButton,
	Toolbar,
	Badge,
    Typography,
    useMediaQuery,
} from "@material-ui/core";
import { useRouter } from "utils";
import { ACCOUNT_PATH } from "features/account";
import Brand from "../brand";
import AccountPopover from "./account-popover";
import { Geolocation } from "features/geolocation";
import { HOME_ROUTE } from "features/home/home-routes";
import { isNotNullOrEmpty } from "utils";
import { PwaInstallPrompt, StyledModal } from "frame/components";
import { ADD_TIMESHEET_ROUTE } from "features/timesheet/timesheet-routes";

// icons
import AccountIconOutlined from "@material-ui/icons/AccountCircleOutlined";
import PeopleAltIcon from "@material-ui/icons/PeopleAlt";
import MenuIcon from "@material-ui/icons/Menu";
import AccessAlarmIcon from "@material-ui/icons/AccessAlarm";
import InfoIcon from "@material-ui/icons/Info";
import Chip from "@material-ui/core/Chip";
import { clearImpersonationToken } from "frame/access-token";
import { isInImpersonationMode } from "frame/access-token";

const useStyles = makeStyles((theme) => ({
	root: {
		backgroundColor: theme.palette.white,
		color: colors.blueGrey[800],
		boxShadow: `0px 2px 4px -1px rgba(0,0,0,0.2), 0px 4px 5px 0px rgba(0,0,0,0.14), 0px 1px 10px 0px rgba(0,0,0,0.12)`,
		position: "relative",
		zIndex: 4, // Ensure top bar appears above menu sidebar
	},
	inner: {
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        position: "relative",
    },
	brand: {
		position: "absolute",
		left: "50%",
		transform: "translateX(-50%)",
        display: "flex",
        justifyContent: "center",
	},
	filler: {
		flex: 1,
	},
	leftSection: {
		display: "flex",
		alignItems: "center",
		flex: 1,
	},
	rightSection: {
		display: "flex",
		alignItems: "center",
		justifyContent: "flex-end",
		flex: 1,
	},
	user: {
		display: "flex",
		flexDirection: "row",
		justifyContent: "spaceBetween",
		alignItems: "center",
		padding: theme.spacing(2),
		minWidth: 260,
	},
	menuIcon: {
		color: theme.palette.light,
		animation: "0.3s all ease-out",
		"&:hover": {
			color: theme.palette.light,
		},
	},
	impersonationIcon: {
		color: theme.palette.error.main,
	},
	// Use the same green as active status in timesheets
	timesheetIcon: {
		color: theme.palette.success.main,
	},
	impersonationChip: {
		"background-color": theme.palette.error.main,
		color: theme.palette.white,
	},
	offlineChip: {
		backgroundColor: theme.palette.error.main,
		color: theme.palette.white,
		marginRight: theme.spacing(1),
		height: 24,
	},
	userAvatar: {
		width: 40,
		height: 40,
	},
	error: {
		background: theme.palette.error.main,
	},
	success: {
		background: theme.palette.success.main,
	},
	devLabel: {
		color: "#1c2b4a",
		fontSize: 12,
		position: "absolute",
		left: 0,
		top: 33,
		[theme.breakpoints.down("xs")]: {
			fontSize: 10,
		},
	},
}));

const TopBar = ({
	profile,
	onOpenNav,
	onSignout,
	hasNetwork,
	timesheetToggle,
	requestQueue,
	profileFetch,
	onAddTimesheetReset,
	onActiveTimesheetRequest,
}) => {
	const classes = useStyles();
	const theme = useTheme();
	const isMobile = useMediaQuery(theme.breakpoints.down("xs"));
	const { history } = useRouter();
	const accountAnchor = useRef();

	const [menu, setMenu] = useState();
	const [openWarningPendingQueueModal, setOpenWarningPendingQueueModal] =
		useState(false);

	const isImpersonating = isInImpersonationMode();

	const onOpenMenu = (value) => () => setMenu(value);

	const onCloseMenu = () => setMenu();

	const isOpen = (value) => value === menu;

	const onLogout = () =>
		isNotNullOrEmpty(requestQueue)
			? setOpenWarningPendingQueueModal(true)
			: onSignout();

	const onOpenActiveTimesheet = () => {
		history.push(ADD_TIMESHEET_ROUTE);
	};

	const onAccount = () => {
		history.push(ACCOUNT_PATH); // todo: dispatch push()
		setMenu(null);
	};

	const onExitImpersonationMode = () => {
		clearImpersonationToken();
		history.push(HOME_ROUTE);
		onAddTimesheetReset();
		profileFetch();
		onActiveTimesheetRequest();
		setMenu(null);
	};

	const isUAT = window.location.href.includes("uat");
	const isDev = window.location.href.includes("dev");
	const isLocal = window.location.href.includes("local");

	const curEnv = isUAT ? "UAT" : isDev ? "Dev" : isLocal ? "Local" : null;

	return (
		<div className={classes.root}>
			<Toolbar className={classes.inner} position="static">
				<div className={classes.leftSection}>
					<Hidden mdUp>
						<IconButton
							data-cy="open-hamburger"
							color="inherit"
							onClick={onOpenNav}
						>
							<MenuIcon />
						</IconButton>
					</Hidden>
				</div>

				<RouterLink
					className={classes.brand}
					to={HOME_ROUTE}
					onClick={(event) => {
						if (isMobile) {
							event.preventDefault();
							onOpenNav();
						}
					}}
				>
					<Brand theme="colored" height={35} />
				</RouterLink>

				<div className={classes.rightSection}>
					{timesheetToggle && (
						<IconButton
							color="inherit"
							onClick={onOpenActiveTimesheet}
						>
							<AccessAlarmIcon className={classes.timesheetIcon} />
						</IconButton>
					)}

					{isImpersonating && (
						<Hidden smDown>
							<Chip
								icon={
									<InfoIcon
										className={classes.impersonationChip}
									/>
								}
								label="Impersonating"
								className={classes.impersonationChip}
							/>
						</Hidden>
					)}

					{!hasNetwork && (
						<Chip
							label="Offline"
							classes={{
								root: classes.offlineChip,
							}}
						/>
					)}

					<IconButton
						id="cypress-my-account"
						aria-label="account of current user"
						aria-controls="menu-account"
						aria-haspopup="true"
						color="inherit"
						ref={accountAnchor}
						onClick={onOpenMenu("account")}
					>
					{hasNetwork ? (
						<Badge
							color="primary"
							variant="dot"
							classes={{
								badge: classes.success,
							}}
						>
							{isImpersonating ? (
								<PeopleAltIcon
									className={
										classes.menuIcon +
										" " +
										classes.impersonationIcon
									}
								/>
							) : (
								<AccountIconOutlined className={classes.menuIcon} />
							)}
						</Badge>
					) : (
						isImpersonating ? (
							<PeopleAltIcon
								className={
									classes.menuIcon +
									" " +
									classes.impersonationIcon
								}
							/>
						) : (
							<AccountIconOutlined className={classes.menuIcon} />
						)
					)}
				</IconButton>
				</div>

				<AccountPopover
					anchor={accountAnchor}
					open={isOpen("account")}
					profile={profile}
					onClose={onCloseMenu}
					onLogout={onLogout}
					onAccount={onAccount}
					onExitImpersonationMode={onExitImpersonationMode}
				/>
			</Toolbar>
			<Geolocation />

			{process.env.NODE_ENV === "production" && <PwaInstallPrompt />}

			<StyledModal
				open={openWarningPendingQueueModal}
				title={`Photo records syncing in progress`}
				onClose={() => setOpenWarningPendingQueueModal(false)}
				onConfirm={onSignout}
				onConfirmText="Logout Anyway"
			>
				{`Please note that you have photo records syncing in progress and will lost if you log out. Do you wish to continue?`}
			</StyledModal>
		</div>
	);
};

TopBar.propTypes = {
	profile: PropTypes.shape({
		firstName: PropTypes.string,
		lastName: PropTypes.string,
		email: PropTypes.string,
	}).isRequired,
	onOpenNav: PropTypes.func,
	onSignout: PropTypes.func,
	profileFetch: PropTypes.func,
	onAddTimesheetReset: PropTypes.func,
	hasNetwork: PropTypes.bool.isRequired,
	timesheetToggle: PropTypes.bool,
	requestQueue: PropTypes.array.isRequired,
};

export default TopBar;
